import { NoShowReasonEnum, NoShowReasonValue } from './no-show-option.type';

import { GpsReport } from './gps-report.type';
import { Location } from './location.type';
import { environment } from 'src/environments/environment';
import { FareTypeEnum } from './login.type';
import { AppData } from '../data';
import { calculateAdditionalFare, calculateRiderFare } from '../helpers/ui-helpers';

export enum JobType {
  Unknown = 0,
  Pickup = 1,
  DropOff = 2
}

export const JobTypeLabels: Record<JobType, string> = {
  [JobType.Pickup]: 'LABEL.pickup',
  [JobType.DropOff]: 'LABEL.dropOff',
  [JobType.Unknown]: 'LABEL.Unknown',
} as const;

export type ScheduleWindow = {
  /** datetime (ISO) */
  start: string
  /** datetime (ISO) */
  end: string
}

export type BaseJob = {
  accountCode: string
  additionalDefaultFareTypeId?:	number
  additionalPassengerSignatureRequired?:	boolean
  additionalRideBalanceDebit?:	number
  /** integer */
  additionalRideCount?:	number
  additionalRideExternalAccountDebit: number
  additionalRideFare: number
  /** integer */
  ambulatorySeats: number
  /** datetime */
  arriveTime:	string
  attendantSignatureRequired: boolean
  authNumber: string
  /** integer */
  companions: number
  defaultFareTypeId: number
  /** datetime */
  departedTime?: string
  driverNote:	string
  driverSignatureRequired: boolean
  dropoffPhone:	string
  /** uuid */
  ePassId:string
  /** integer */
  escorts: number
  /** datetime */
  estimatedTimeOfArrival:	string
  externalAccount: ExternalAccount
  flagStopId: string;
  guardianSignatureRequired:	boolean
  jobEnabled: boolean
  jobType: JobType
  liftRequired: boolean
  location: Location
  manualSignatureRequired:	boolean
  note:	string
  /** integer */
  numberOfChildren:	number
  /** datetime */
  onTheWayTime:	string
  performed: boolean;
  /** datetime */
  performTime: string
  phone:	string
  pickupPhone: string
  /** Only received for pickup jobs; null/undefined when there is no window */
  pickupWindow?: ScheduleWindow | null
  /** Only received for dropoff jobs; null/undefined when there is no window */
  dropoffWindow?: ScheduleWindow | null
  priority:	string
  /** datetime */
  priorityTime:	string
  rideComments:	string
  rideFare:	number
  ridePaymentIsSuccessful?: boolean
  /** integer */
  rideId:	number
  riderId: number | string
  riderAccountBalance: number
  riderBalanceDebit: number
  riderComments: string
  riderExternalAccountDebit: number
  riderImageId?: string
  riderLastName: string
  riderFirstName: string
  riderPhone: string
  riderSignatureRequired: boolean
  /** integer */
  sequence?: number
  /** datetime */
  scheduledTime: string
  specialAssistances: string[]
  /** integer */
  stopDuration: number
  /** integer */
  stopId?:	number
  /** Determines what the job type is for V2 Model -- Ecolane */
  stopType?:	StopTypeEnum
  /** integer */
  stretcherCount: number
  supportsDuplicatingNoShow: boolean
  travelAloneGuardianSignatureRequired?: boolean
  /** integer */
  units: number
  wheelchair: boolean
  /** integer */
  wheelchairSeats: number
  willCall:	boolean
  willCallReady:	boolean
  readOnly: true

  //  FOR UI
  completed?: boolean
}

export enum StopTypeEnum {
  JOB = '0',
  SCHEDULED_FLAG_STOP = '1',
  PULL_OUT = '2',
  PULL_IN = '3',
  OUT_OF_SERVICE_START = '4',
  OUT_OF_SERVICE_END = '5',
  WAYPOINT = '6',
  MONITOR_PICKUP = '7',
  MONITOR_DROPOFF = '8',
  BREAK_WITH_LOCATION = '9',
  BREAK_WITHOUT_LOCATION = '10'
}

export type ExternalAccount = {
  /** integer */
  id: number
  name: string
}

export type JobPerform = {
  additionalFareTypeId?: number;
  additionalRideFare: number;
  additionalRideFareHandleType: FareTypeEnum;
  externalPaymentMethodId?: number
  additionalRideCount?: number
  fareCollected: number;
  fareTypeId: number;
  flagStopId: string;
  flagStopPassScannedWhileOffline: boolean;
  gpsReport: GpsReport;
  jobId: string;
  jobType: JobType;
  noShowReason: NoShowReasonValue;
  noShowReasonText: string;
  numberOfChildren: number;
  numberOfEscorts: number;
  numberOfPasses: number;
  passId?: string;
  rideFare: number;
  rideFareHandleType: FareTypeEnum;
  rideId: number;
  riderId: number | string;
  signature: string;
  signatureReason: string;
  stopId?: number;
  stopType: StopTypeEnum
  units: number;
  driverNotes?: string | null;
}

export function buildJobPerform({ job, data, signature, signatureReason, noShowReason, noShowReasonText }
  : { job: BaseJob, data: AppData, signature: string, signatureReason: string, noShowReason?: NoShowReasonEnum, noShowReasonText?: string })
  : JobPerform {

    const _additionalDefaultFareTypeId = job.additionalDefaultFareTypeId ?? null;
    const additionalDefaultFareTypeId = _additionalDefaultFareTypeId === null ? null : Number(_additionalDefaultFareTypeId);

    const _defaultFareTypeId = job.defaultFareTypeId ?? null;
    const defaultFareTypeId = _defaultFareTypeId === null ? null : Number(_defaultFareTypeId);

    const getHandleTypeFromFareTypeId = (fareTypeId: number): FareTypeEnum => {
      const fareType = data.fareTypes.find(type => type.id === fareTypeId);
      return fareType?.type;
    };

    const rideFareHandleType = defaultFareTypeId === null ? null : getHandleTypeFromFareTypeId(defaultFareTypeId);
    const additionalRideFareHandleType = additionalDefaultFareTypeId === null ? null : getHandleTypeFromFareTypeId(additionalDefaultFareTypeId);
    const defaultFareCollected = rideFareHandleType === FareTypeEnum.NORMAL ? (job.rideFare || 0) : 0;
    const additionalDefaultFareCollected = additionalRideFareHandleType === FareTypeEnum.NORMAL ? (job.additionalRideFare || 0) : 0;

  return {
    additionalFareTypeId: additionalDefaultFareTypeId === null
      ? defaultFareTypeId
      : additionalDefaultFareTypeId,
    externalPaymentMethodId: job.externalAccount?.id ?? null,

    rideFare: calculateRiderFare(job),
    rideFareHandleType: rideFareHandleType,
    additionalRideFare: calculateAdditionalFare(job),
    additionalRideFareHandleType: additionalRideFareHandleType,
    fareTypeId: job.defaultFareTypeId === null ? null : defaultFareTypeId,
    fareCollected: defaultFareCollected + additionalDefaultFareCollected,
    additionalRideCount: job.additionalRideCount,

    flagStopId: job.flagStopId,
    flagStopPassScannedWhileOffline: false,
    gpsReport: null,
    jobId: job.stopId?.toString() ?? '',
    noShowReason: noShowReason || null,
    noShowReasonText: noShowReasonText ?? null,
    jobType: job.jobType,
    numberOfChildren: job.numberOfChildren,
    numberOfEscorts: (<BaseJob>job).escorts ?? job.companions,
    numberOfPasses: 1,
    passId: null,
    rideId: job.rideId,
    riderId: job.riderId,
    signature: signature,
    signatureReason: signatureReason,
    stopId: job.stopId,
    stopType: job.stopType,
    units: job.units,
  };
}

export type JobArrive = {
  rideId: number;
  flagStopId: string;
  jobType: JobType;
  gpsReport: GpsReport;
  stopType: StopTypeEnum;
  stopId: number;
}

export type JobDeparture = {
  rideId: number;
  flagStopId: string;
  jobType: JobType;
  gpsReport: GpsReport;
  stopType: StopTypeEnum;
  stopId: number;
}

export function buildJobArrive({ job, gpsReport }: { job: BaseJob, gpsReport: GpsReport }): JobArrive {
  return {
    rideId: job.rideId,
    flagStopId: job.flagStopId,
    gpsReport: gpsReport,
    jobType: job.jobType,
    stopId: job.stopId,
    stopType: job.stopType,
  };
}

export function buildJobDepart({ job, gpsReport }: { job: BaseJob, gpsReport: GpsReport }): JobArrive {
  return {
    rideId: job.rideId,
    flagStopId: job.flagStopId,
    gpsReport: gpsReport,
    jobType: job.jobType,
    stopId: job.stopId,
    stopType: job.stopType,
  };
}

export type OnTheWay = {
  rideId: number;
  gpsReport: GpsReport;
  estimatedTimeOfArrival?: Date;
}
export function buildOnTheWay({ job: { rideId }, estimatedTimeOfArrival, gpsReport }: { job: BaseJob, estimatedTimeOfArrival: Date, gpsReport: GpsReport }): OnTheWay {
  return { rideId, estimatedTimeOfArrival, gpsReport };
}
